import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ArrowRight,
  Check,
  Heart,
  UtensilsCrossed,
  Coffee,
  Utensils,
  Soup,
  Milk,
  Users,
  Cookie,
  Bed,
} from "lucide-react";
import { PageShell } from "@project/user/components/page-shell";
import { ManavSevaBankDetails } from "@project/user/components/manav-seva-bank-details";
import {
  Reveal,
  RevealText,
  Stagger,
  StaggerItem,
} from "@project/user/components/motion";
import { BhojanSponsorshipModal } from "@project/user/components/bhojan-sponsorship-modal";
import { Button } from "@project/components/ui/button";
import { useI18n } from "@project/lib/use-i18n";
import { useAuth } from "@project/hooks/useAuth";
import { toast } from "sonner";
import { publicBhojanService } from "@project/services/donate/bhojan.service";
import {
  formatBhojanPrice,
  isBhojanBookingWindowClosed,
  normalizeBhojanSponsorshipType,
  type BhojanPackage,
  type BhojanScheme,
  type BhojanSponsorshipSelection,
} from "@project/types/bhojan";
import mealImg from "../../../assets/bhojanshala.png";

/** Shared hover: blue border + soft lift (matches reference cards) */
const cardHoverClass =
  "border border-border/80 bg-card transition-all duration-300 hover:-translate-y-1 hover:border-primary hover:shadow-[0_12px_28px_-10px_rgba(29,74,138,0.28)]";

/** Amount / price — normal sans, large bold (avoid <p> so .jivandhara-site p does not shrink it) */
const amountClass =
  "block font-sans text-[1.75rem] font-bold leading-none tracking-tight sm:text-[2rem]";

const HIGHLIGHT_ORANGE = "#E38018";

function sortSchemes(schemes: BhojanScheme[]) {
  return [...schemes]
    .filter(scheme => scheme.is_active !== false)
    .sort((a, b) => {
      const orderDiff = (a.display_order ?? 0) - (b.display_order ?? 0);
      if (orderDiff !== 0) {
        return orderDiff;
      }
      if (a.is_highlighted !== b.is_highlighted) {
        return a.is_highlighted ? 1 : -1;
      }
      return a.id - b.id;
    });
}

function getPackageIcon(name?: string) {
  const n = (name || "").toLowerCase();
  if (
    n.includes("tea") ||
    n.includes("breakfast") ||
    n.includes("સવાર") ||
    n.includes("નાસ્તો") ||
    n.includes("ચા")
  ) {
    return <Coffee className="h-5 w-5" />;
  }
  if (n.includes("milk") || n.includes("દૂધ")) {
    return <Milk className="h-5 w-5" />;
  }
  if (
    n.includes("tiffin") ||
    n.includes("ટીફીન") ||
    n.includes("ટિફિન") ||
    n.includes("senior")
  ) {
    return <Users className="h-5 w-5" />;
  }
  if (
    n.includes("dessert") ||
    n.includes("mithai") ||
    n.includes("sweet") ||
    n.includes("મિઠાઈ") ||
    n.includes("મીઠાઈ")
  ) {
    return <Cookie className="h-5 w-5" />;
  }
  if (
    n.includes("sick") ||
    n.includes("bedridden") ||
    n.includes("માંદા") ||
    n.includes("અસ્વસ્થ")
  ) {
    return <Bed className="h-5 w-5" />;
  }
  if (
    n.includes("dal") ||
    n.includes("khichdi") ||
    n.includes("ખીચડી") ||
    n.includes("farsan") ||
    n.includes("ફરસાણ")
  ) {
    return <Soup className="h-5 w-5" />;
  }
  if (
    n.includes("afternoon") ||
    n.includes("evening") ||
    n.includes("simple meal") ||
    n.includes("બપોર") ||
    n.includes("સાંજ") ||
    n.includes("ભોજન")
  ) {
    return <UtensilsCrossed className="h-5 w-5" />;
  }
  return <Utensils className="h-5 w-5" />;
}

function SchemeCard({
  scheme,
  sponsorLabel,
  onSponsor,
}: {
  scheme: BhojanScheme;
  sponsorLabel: string;
  onSponsor: () => void;
}) {
  const highlighted = scheme.is_highlighted === true;
  const features = Array.isArray(scheme.features)
    ? scheme.features.map(item => String(item).trim()).filter(Boolean)
    : [];

  return (
    <article
      className={`relative flex h-full flex-col rounded-2xl bg-card p-6 transition-all duration-300 hover:-translate-y-1 ${highlighted
        ? "shadow-[0_8px_30px_-12px_rgba(227,128,24,0.2)] hover:shadow-[0_14px_32px_-10px_rgba(227,128,24,0.32)]"
        : `${cardHoverClass} shadow-[0_8px_30px_-12px_rgba(0,0,0,0.12)]`
        }`}
      style={
        highlighted ? { border: `2px solid ${HIGHLIGHT_ORANGE}` } : undefined
      }
    >

      <span
        className={`w-fit rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] ${highlighted ? "bg-[#fff1e0]" : "bg-primary/10 text-primary"
          }`}
        style={highlighted ? { color: HIGHLIGHT_ORANGE } : undefined}
      >
        {scheme.badge_text}
      </span>

      <h3 className="mt-4 font-serif !text-[1.7rem] !font-bold leading-snug text-foreground sm:text-xl">
        {scheme.title}
      </h3>
      <p className="mt-1.5 !text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {scheme.technical_name}
      </p>

      <div className="border-border/80 my-5 border-t" />

      <div
        className={`${amountClass} ${highlighted ? "!text-2xl" : "text-primary !text-2xl"}`}
        style={highlighted ? { color: HIGHLIGHT_ORANGE } : undefined}
      >
        {formatBhojanPrice(scheme.price)}
      </div>
      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        {scheme.sum_of_rupees}
      </p>

      {/* <ul className="mt-5 flex-1 space-y-3">
        {features.map(feature => (
          <li
            key={feature}
            className="text-foreground/80 flex items-start gap-2.5 text-sm leading-relaxed"
          >
            <Check
              className={`mt-0.5 h-4 w-4 shrink-0 ${highlighted ? "" : "text-emerald-600"
                }`}
              style={highlighted ? { color: HIGHLIGHT_ORANGE } : undefined}
              strokeWidth={2.5}
            />
            <span>{feature}</span>
          </li>
        ))}
      </ul> */}

      <Button
        type="button"
        onClick={onSponsor}
        className={`mt-7 h-11 w-full rounded-xl text-sm font-bold uppercase tracking-[0.08em] ${highlighted
          ? "text-white hover:opacity-90"
          : "hover:bg-primary/90 bg-primary text-primary-foreground"
          }`}
        style={highlighted ? { backgroundColor: HIGHLIGHT_ORANGE } : undefined}
      >
        {sponsorLabel}
      </Button>
    </article>
  );
}

export default function BhojanshalaPage() {
  const { t } = useI18n();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schemes, setSchemes] = useState<BhojanScheme[]>([]);
  const [mealPackages, setMealPackages] = useState<BhojanPackage[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selection, setSelection] = useState<BhojanSponsorshipSelection | null>(
    null
  );

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      setLoading(true);
      try {
        const [schemesRes, otherRes] = await Promise.all([
          publicBhojanService.listActiveSchemes(),
          publicBhojanService.listActivePackages({
            sponsorships_type: "other",
          }),
        ]);
        if (ignore) {
          return;
        }
        setSchemes(sortSchemes(schemesRes.data ?? []));

        const otherData = otherRes.data ?? [];
        setMealPackages(
          otherData.filter(
            pkg =>
              normalizeBhojanSponsorshipType(pkg.sponsorships_type) !==
              "special"
          )
        );
      } catch {
        if (!ignore) {
          setSchemes([]);
          setMealPackages([]);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    };
    void load();
    return () => {
      ignore = true;
    };
  }, []);

  const openSponsorship = (next: BhojanSponsorshipSelection) => {
    if (!isAuthenticated) {
      toast.info("Please log in to continue with Bhojanshala donation");
      navigate({
        to: "/login",
        search: { redirectTo: "/bhojanshala" },
      });
      return;
    }
    setSelection(next);
    setModalOpen(true);
  };

  const openPackage = (pkg: BhojanPackage) => {
    const isSpecial =
      normalizeBhojanSponsorshipType(pkg.sponsorships_type) === "special";
    openSponsorship({
      kind: "package",
      id: pkg.id,
      title: pkg.name,
      description: pkg.description,
      price: pkg.price,
      categoryLabel: isSpecial
        ? t("programs.bhojanshala.special.heading")
        : t("programs.bhojanshala.packages.heading"),
      servicesLabel: pkg.description,
      packageType: "Complete one-day package",
      cutOff: pkg.cut_off,
    });
  };

  const openScheme = (scheme: BhojanScheme) => {
    openSponsorship({
      kind: "scheme",
      id: scheme.id,
      title: scheme.title,
      description: scheme.features?.[0],
      price: scheme.price,
      categoryLabel: t("programs.bhojanshala.schemes.heading"),
      badgeText: scheme.badge_text,
      technicalName: scheme.technical_name,
    });
  };

  const scrollToSponsorships = () => {
    document
      .getElementById("bhojanshala-sponsorships")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PageShell>
      <section className="bg-hero-gradient relative overflow-hidden text-primary-foreground shadow-[0_8px_24px_-8px_rgba(0,0,0,0.18)]">
        <div className="anim-fade absolute inset-0">
          <img
            src={mealImg}
            alt=""
            aria-hidden
            loading="eager"
            className="h-full w-full object-cover opacity-10"
          />
        </div>
        <div className="from-primary/55 via-primary/40 to-gold/35 absolute inset-0 bg-gradient-to-br" />
        <div className="from-primary/50 to-primary/25 absolute inset-0 bg-gradient-to-t via-transparent" />

        <div className="relative mx-auto grid max-w-site items-center gap-10 px-4 py-12 sm:py-14 md:grid-cols-2 md:gap-12 md:py-16 lg:gap-16">
          <div className="text-left">
            <span
              className="bg-gold/25 border-gold/40 anim-fade-up inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-gold"
              style={{ animationDelay: "120ms" }}
            >
              <UtensilsCrossed className="h-3.5 w-3.5" />
              {t("programs.bhojanshala.hero.badge")}
            </span>

            <h1 className="mt-5 font-serif text-4xl font-bold leading-tight drop-shadow sm:text-5xl md:text-[3.25rem]">
              <span className="anim-line" style={{ animationDelay: "240ms" }}>
                <span>{t("programs.bhojanshala.hero.brand")}</span>
              </span>
            </h1>

            <p
              className="anim-fade-up mt-4 text-base font-semibold leading-snug text-gold sm:text-lg"
              style={{ animationDelay: "400ms" }}
            >
              {t("programs.bhojanshala.hero.title")}
            </p>

            <p
              className="text-primary-foreground/95 anim-fade-up mt-4 max-w-xl text-sm leading-relaxed sm:text-base"
              style={{ animationDelay: "520ms" }}
            >
              {t("programs.bhojanshala.hero.subtitle")}
            </p>

            <div
              className="anim-fade-up mt-8 flex flex-wrap gap-3"
              style={{ animationDelay: "660ms" }}
            >
              <Button
                type="button"
                onClick={scrollToSponsorships}
                className="hover:bg-gold/90 btn-lift h-11 gap-2 rounded-xl bg-gold px-5 font-semibold text-gold-foreground"
              >
                <Heart className="h-4 w-4 fill-current" />
                {t("programs.bhojanshala.hero.primaryCta")}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={scrollToSponsorships}
                className="border-primary-foreground/50 hover:bg-primary-foreground/10 btn-lift h-11 gap-2 rounded-xl bg-transparent px-5 font-semibold text-primary-foreground hover:text-primary-foreground"
              >
                <ArrowRight className="h-4 w-4" />
                {t("programs.bhojanshala.hero.secondaryCta")}
              </Button>
            </div>
          </div>

          <div
            className="anim-zoom-in relative"
            style={{ animationDelay: "380ms" }}
          >
            <img
              src={mealImg}
              alt={t("programs.bhojanshala.hero.imageAlt")}
              className="w-[80%] rounded-2xl object-cover shadow-[0_20px_50px_-20px_rgba(0,0,0,0.55)] ring-1 ring-white/20"
            />
          </div>
        </div>
      </section>

      {/* About */}
      <section className="mx-auto max-w-site px-4 py-14 sm:py-16">
        <Reveal variant="wipe-left">
          <div className="grid overflow-hidden rounded-[1.75rem] border border-border bg-card shadow-sm md:grid-cols-2">
            <div className="flex flex-col justify-center gap-3 p-8 sm:p-10 lg:p-12">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-primary">
                {t("programs.bhojanshala.about.eyebrow")}
              </p>
              <h2 className="font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                {t("programs.bhojanshala.about.heading")}
              </h2>
              <p className="text-base text-muted-foreground">
                {t("programs.bhojanshala.about.tagline")}
              </p>
            </div>
            <div className="border-border/70 flex items-center border-t p-8 sm:p-10 md:border-l md:border-t-0 lg:p-12">
              <p className="text-[0.95rem] leading-relaxed text-muted-foreground">
                {t("programs.bhojanshala.about.body")}
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {loading ? (
        <div className="mx-auto max-w-site px-4 pb-20 text-center text-sm text-muted-foreground">
          {t("programs.bhojanshala.loading")}
        </div>
      ) : (
        <>
          {/* Permanent Meal Sponsorship — schemes */}
          <section
            id="bhojanshala-sponsorships"
            className="border-border/40 bg-secondary/30 border-y py-14 sm:py-16"
          >
            <div className="mx-auto max-w-site px-4">
              <Reveal variant="morph">
                <div className="mx-auto max-w-3xl text-center">
                  <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
                    {t("programs.bhojanshala.schemes.eyebrow")}
                  </p>
                  <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                    {t("programs.bhojanshala.schemes.heading")}
                  </h2>
                  <p className="mt-3 text-muted-foreground">
                    {t("programs.bhojanshala.schemes.subtitle")}
                  </p>
                </div>
              </Reveal>

              {schemes.length === 0 ? (
                <p className="mt-10 text-center text-sm text-muted-foreground">
                  {t("programs.bhojanshala.schemes.empty")}
                </p>
              ) : (
                <Stagger
                  className="mt-10 grid gap-6 sm:grid-cols-2  md:grid-cols-3 lg:grid-cols-4"
                  step={130}
                >
                  {schemes.map((scheme, index) => (
                    <StaggerItem
                      key={scheme.id}
                      index={index}
                      className="h-full"
                    >
                      <SchemeCard
                        scheme={scheme}
                        sponsorLabel={t(
                          "programs.bhojanshala.schemes.sponsorNow"
                        )}
                        onSponsor={() => openScheme(scheme)}
                      />
                    </StaggerItem>
                  ))}
                </Stagger>
              )}
            </div>
          </section>

          {/* Other Meal Sponsorship Options */}
          <section className="mx-auto max-w-site px-4 py-14 sm:py-16">
            <Reveal variant="fade">
              <div className="mx-auto max-w-3xl text-center">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-accent">
                  {t("programs.bhojanshala.packages.eyebrow")}
                </p>
                <h2 className="mt-3 font-serif text-3xl font-semibold text-foreground sm:text-4xl">
                  <RevealText delay={100}>
                    {t("programs.bhojanshala.packages.heading")}
                  </RevealText>
                </h2>
                <p className="mt-3 text-muted-foreground">
                  {t("programs.bhojanshala.packages.subtitle")}
                </p>
              </div>
            </Reveal>

            {mealPackages.length === 0 ? (
              <p className="mt-10 text-center text-sm text-muted-foreground">
                {t("programs.bhojanshala.packages.empty")}
              </p>
            ) : (
              <>
                {/* NEW TABLE DESIGN (Matching attached specification) */}
                <div className="mx-auto mt-10 max-w-4xl overflow-hidden rounded-2xl border border-border/70 bg-card shadow-xs sm:rounded-3xl">
                  {/* Table Header for Desktop */}
                  <div className="hidden grid-cols-[44px_minmax(0,1fr)_120px_130px] items-center gap-4 border-b border-border/70 bg-background px-6 py-4 text-[12px] font-extrabold uppercase tracking-wider text-primary lg:grid">
                    <div className="text-center">
                      {t("programs.bhojanshala.packages.colIndex")}
                    </div>
                    <div>
                      {t("programs.bhojanshala.packages.colService")}
                    </div>
                    {/* <div>
                      {t("programs.bhojanshala.packages.colDescription")}
                    </div> */}
                    <div>
                      {t("programs.bhojanshala.packages.colAmount")}
                    </div>
                    {/* <div>
                      {t("programs.bhojanshala.packages.colAvailability")}
                    </div> */}
                    <div className="text-right pr-1">
                      {t("programs.bhojanshala.packages.colAction")}
                    </div>
                  </div>

                  {/* Table Body Rows */}
                  <Stagger className="divide-border/40 divide-y" step={80}>
                    {mealPackages.map((pkg, index) => {
                      const bookingClosed = isBhojanBookingWindowClosed(
                        pkg.cut_off
                      );

                      return (
                        <StaggerItem
                          key={pkg.id}
                          index={index}
                          from="left"
                          className="group px-4 py-3 transition-colors duration-150 hover:bg-background sm:px-6 sm:py-4.5"
                        >
                          {/* Desktop Row */}
                          <div className="hidden grid-cols-[44px_minmax(0,1fr)_120px_130px] items-center gap-4 lg:grid">
                            {/* 1. # Index */}
                            <div className="text-center font-medium text-muted-foreground">
                              {index + 1}
                            </div>

                            {/* 2. Service Icon & Name */}
                            <div className="flex items-center gap-3 pr-2">
                              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                                {getPackageIcon(pkg.name)}
                              </div>
                              <span className=" text-[15px] !font-semibold text-foreground">
                                {pkg.name}
                              </span>
                            </div>

                            {/* 3. Description / Category (hidden) */}
                            {/* <div className="text-sm text-muted-foreground pr-2">
                              {pkg.description || "—"}
                            </div> */}

                            {/* 4. Amount */}
                            <div className="font-sans text-base font-extrabold text-primary">
                              {formatBhojanPrice(pkg.price)}
                            </div>

                            {/* 5. Availability */}
                            {/* <div className="flex items-center gap-2 text-xs font-semibold">
                              <span
                                className={`h-2 w-2 shrink-0 rounded-full ${bookingClosed
                                  ? "bg-amber-500"
                                  : "bg-emerald-600"
                                  }`}
                              />
                              <span
                                className={
                                  bookingClosed
                                    ? "text-amber-600 dark:text-amber-400"
                                    : "text-emerald-700 dark:text-emerald-400"
                                }
                              >
                                {bookingClosed
                                  ? t(
                                    "programs.bhojanshala.packages.nextAvailable"
                                  )
                                  : t(
                                    "programs.bhojanshala.packages.availableToday"
                                  )}
                              </span>
                            </div> */}

                            {/* 6. Action */}
                            <div className="flex justify-end pr-1">
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="w-full max-w-[115px] rounded-full border-primary/40 bg-background px-3 py-1.5 text-xs font-bold text-primary shadow-xs transition-all hover:bg-primary hover:text-primary-foreground hover:shadow-sm"
                                onClick={() => openPackage(pkg)}
                              >
                                {t("programs.bhojanshala.packages.sponsorNow")}
                              </Button>
                            </div>
                          </div>

                          {/* Mobile / Tablet Card View */}
                          <div className="flex flex-col gap-3 lg:hidden">
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-center gap-2.5">
                                <span className="text-xs font-bold text-muted-foreground/80">
                                  #{index + 1}
                                </span>
                                <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
                                  {getPackageIcon(pkg.name)}
                                </div>
                                <h3 className="font-serif text-base font-bold text-foreground">
                                  {pkg.name}
                                </h3>
                              </div>
                              <div className="text-base font-extrabold text-primary">
                                {formatBhojanPrice(pkg.price)}
                              </div>
                            </div>

                            {/* {pkg.description && (
                              <p className="pl-7 text-xs text-muted-foreground">
                                {pkg.description}
                              </p>
                            )} */}

                            <div className="flex items-center justify-between gap-3 border-t border-border/30 pt-2 pl-7">
                              <div className="flex items-center gap-1.5 text-xs font-semibold">
                                <span
                                  className={`h-2 w-2 shrink-0 rounded-full ${bookingClosed
                                    ? "bg-amber-500"
                                    : "bg-emerald-600"
                                    }`}
                                />
                                <span
                                  className={
                                    bookingClosed
                                      ? "text-[11px] text-amber-600"
                                      : "text-[11px] text-emerald-700"
                                  }
                                >
                                  {bookingClosed
                                    ? t(
                                      "programs.bhojanshala.packages.nextAvailable"
                                    )
                                    : t(
                                      "programs.bhojanshala.packages.availableToday"
                                    )}
                                </span>
                              </div>

                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="rounded-full border-primary/40 bg-background px-4 py-1 text-xs font-bold text-primary hover:bg-primary hover:text-primary-foreground"
                                onClick={() => openPackage(pkg)}
                              >
                                {t("programs.bhojanshala.packages.sponsorNow")}
                              </Button>
                            </div>
                          </div>
                        </StaggerItem>
                      );
                    })}
                  </Stagger>
                </div>

                {/* OLD CARD GRID DESIGN (Preserved as commented-out code) */}
                {/*
                <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                  {mealPackages.map((pkg, index) => {
                    const bookingClosed = isBhojanBookingWindowClosed(
                      pkg.cut_off
                    );
                    return (
                      <Reveal key={pkg.id} delay={index * 60}>
                        <article
                          className={`flex h-full flex-col rounded-2xl p-6 shadow-sm ${cardHoverClass}`}
                        >
                          <h3 className="font-serif text-xl font-semibold text-foreground">
                            {pkg.name}
                          </h3>
                          {pkg.description ? (
                            <p className="mt-2 flex-1 text-sm text-muted-foreground">
                              {pkg.description}
                            </p>
                          ) : (
                            <div className="flex-1" />
                          )}

                          <div className="mt-5 flex items-start justify-between gap-3">
                            <div>
                              <div
                                className={`${amountClass} !text-xl text-primary`}
                              >
                                {formatBhojanPrice(pkg.price)}
                              </div>
                              <p
                                className={`mt-1.5 flex items-center gap-1.5 text-xs font-medium ${
                                  bookingClosed
                                    ? "text-accent"
                                    : "text-emerald-700"
                                }`}
                              >
                                <span
                                  className={`h-1.5 w-1.5 rounded-full ${
                                    bookingClosed ? "bg-accent" : "bg-emerald-600"
                                  }`}
                                />
                                {bookingClosed
                                  ? t(
                                      "programs.bhojanshala.packages.nextAvailable"
                                    )
                                  : t(
                                      "programs.bhojanshala.packages.availableToday"
                                    )}
                              </p>
                            </div>
                            <span className="pt-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                              {t("programs.bhojanshala.packages.sponsorship")}
                            </span>
                          </div>

                          <Button
                            type="button"
                            variant="outline"
                            className="border-primary/50 mt-5 w-full rounded-full bg-background text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
                            onClick={() => openPackage(pkg)}
                          >
                            {t("programs.bhojanshala.packages.sponsorNow")}
                          </Button>
                        </article>
                      </Reveal>
                    );
                  })}
                </div>
                */}
              </>
            )}
          </section>

          {/* Supporting donor */}
          <section className="mx-auto max-w-site px-4 py-14 sm:py-16">
            <Reveal variant="morph">
              <div className="border-primary/20 overflow-hidden rounded-[1.75rem] border bg-card shadow-sm">
                <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1.4fr_1fr] lg:items-center lg:gap-10 lg:p-10">
                  <div>
                    <span className="bg-primary/15 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-[0.14em] text-primary">
                      <Heart className="h-3 w-3 fill-current" />
                      {t("programs.bhojanshala.supporting.badge")}
                    </span>
                    <h2 className="mt-4 font-serif text-3xl font-semibold text-primary sm:text-4xl">
                      {t("programs.bhojanshala.supporting.heading")}
                    </h2>
                    <p className="mt-3 max-w-xl text-muted-foreground">
                      {t("programs.bhojanshala.supporting.body")}
                    </p>
                  </div>

                  <div className="rounded-2xl border border-border bg-background p-6 text-center shadow-sm">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                      {t("programs.bhojanshala.supporting.contribution")}
                    </p>
                    <div
                      className={`mt-2 ${amountClass} text-primary sm:text-[2.25rem]`}
                    >
                      {t("programs.bhojanshala.supporting.amount")}
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {t("programs.bhojanshala.supporting.amountLabel")}
                    </p>
                    <Button
                      type="button"
                      className="hover:bg-primary/90 btn-lift mt-5 w-full rounded-xl bg-primary uppercase tracking-wide text-primary-foreground"
                      onClick={() =>
                        openSponsorship({
                          kind: "supporting",
                          title: t("programs.bhojanshala.supporting.heading"),
                          description: t(
                            "programs.bhojanshala.supporting.body"
                          ),
                          price: "9000",
                          categoryLabel: t(
                            "programs.bhojanshala.supporting.badge"
                          ),
                        })
                      }
                    >
                      {t("programs.bhojanshala.supporting.cta")}
                    </Button>
                  </div>
                </div>
              </div>
            </Reveal>
          </section>
        </>
      )}

      {/* Bank & UPI Details */}
      <ManavSevaBankDetails />

      <BhojanSponsorshipModal
        open={modalOpen}
        selection={selection}
        onOpenChange={open => {
          setModalOpen(open);
          if (!open) {
            setSelection(null);
          }
        }}
      />
    </PageShell>
  );
}
